/**
 * KidiCare quote form — prefill, draft save/restore, duplicate-submit guard.
 * QUOTE-FLOW-SPEC.md. Never submits the form itself; never modifies the
 * cart (read-only fetch of /cart.js). No PII is written into any URL.
 * v2 (2026-09-11): a product or cart link always adds its prefill. The saved
 * draft keeps typed details; the message is replaced only while it still
 * equals the previous prefill (i.e. the shopper has not edited it).
 */
(function () {
  'use strict';

  var DRAFT_KEY = 'kcQuoteDraftV2';

  function getParam(name) {
    var params = new URLSearchParams(window.location.search);
    return params.get(name);
  }

  function refPathOnly(url) {
    try {
      var u = new URL(url, window.location.origin);
      return u.pathname;
    } catch (e) {
      return '';
    }
  }

  function init() {
    // Shopify adds ?contact_posted=true after a successful submission:
    // forget the draft so the next quote starts clean.
    if (getParam('contact_posted') === 'true') {
      try {
        sessionStorage.removeItem(DRAFT_KEY);
      } catch (e) {}
    }

    var wrap = document.getElementById('kc-quote-form-wrap');
    if (!wrap) return;
    var form = document.getElementById('kc-quote-form');
    if (!form) return;

    var bodyField = document.getElementById('kc-quote-body');
    var sourceField = document.getElementById('kc-quote-source');
    var contextField = document.getElementById('kc-quote-context');
    var languageField = form.querySelector('input[name="contact[Language]"]');
    var submitBtn = document.getElementById('kc-quote-submit');

    var lang = ((languageField && languageField.value) || document.documentElement.lang || '').toLowerCase();
    var isFr = lang.indexOf('fr') === 0;
    var T = isFr
      ? {
          product: 'Produit : ',
          subtotal: 'Sous-total du panier affiché en ligne : ',
          note: ' (soumission non contraignante; livraison et taxes non incluses)'
        }
      : {
          product: 'Product: ',
          subtotal: 'Cart subtotal shown online: ',
          note: ' (not a binding quote; shipping and tax not included)'
        };

    var trackedFieldIds = [
      'kc-quote-name',
      'kc-quote-email',
      'kc-quote-body',
      'kc-quote-location',
      'kc-quote-organization',
      'kc-quote-household',
      'kc-quote-phone',
      'kc-quote-timeline',
      'kc-quote-quantities',
      'kc-quote-budget'
    ];

    var lastPrefill = '';
    var context = 'none';

    // Hidden "Source page": ?source= param, else document.referrer path only.
    var sourceParam = getParam('source');
    if (sourceField) {
      if (sourceParam) {
        sourceField.value = sourceParam;
      } else if (document.referrer) {
        sourceField.value = refPathOnly(document.referrer);
      }
    }

    function setContext(type) {
      context = type;
      if (contextField) contextField.value = type;
    }

    function saveDraft() {
      var draft = {};
      trackedFieldIds.forEach(function (id) {
        var el = document.getElementById(id);
        if (!el) return;
        draft[id] = el.type === 'checkbox' ? el.checked : el.value;
      });
      draft._prefill = lastPrefill;
      draft._context = context;
      try {
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      } catch (e) {
        // sessionStorage unavailable — the form still works without drafts.
      }
    }

    function loadDraft() {
      try {
        var raw = sessionStorage.getItem(DRAFT_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    }

    function restoreDraft(draft) {
      trackedFieldIds.forEach(function (id) {
        if (!(id in draft)) return;
        var el = document.getElementById(id);
        if (!el) return;
        if (el.type === 'checkbox') {
          el.checked = !!draft[id];
        } else {
          el.value = draft[id];
        }
      });
      if (typeof draft._prefill === 'string') lastPrefill = draft._prefill;
      if (typeof draft._context === 'string') setContext(draft._context);
    }

    function applyPrefill(text) {
      if (!bodyField || !text) return;
      var current = bodyField.value;
      if (!current || current === lastPrefill) {
        // Empty, or still exactly the previous prefill: replace it.
        bodyField.value = text;
        lastPrefill = text;
      } else if (current.indexOf(text) === -1) {
        // The shopper has written their own text: keep it, add the new lines.
        bodyField.value = current + '\n' + text;
      }
      saveDraft();
    }

    function formatMoney(cents) {
      var amount = ((cents || 0) / 100).toFixed(2);
      return isFr ? amount.replace('.', ',') + ' $' : '$' + amount;
    }

    function runPrefill() {
      var productHandle = getParam('product');
      var variantId = getParam('variant');
      var qty = getParam('qty') || '1';
      var fromCart = getParam('from') === 'cart';

      if (productHandle) {
        setContext('product');
        fetch('/products/' + encodeURIComponent(productHandle) + '.js', { credentials: 'same-origin' })
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .then(function (product) {
            if (!product) return;
            var variant = null;
            if (variantId && product.variants) {
              variant = product.variants.filter(function (v) {
                return String(v.id) === String(variantId);
              })[0];
            }
            if (!variant && product.variants && product.variants.length) {
              variant = product.variants[0];
            }
            var line = T.product + product.title;
            if (variant && variant.title && variant.title !== 'Default Title') {
              line += ' — ' + variant.title;
            }
            if (variant && variant.sku) {
              line += ' (SKU ' + variant.sku + ')';
            }
            line += ' × ' + qty;
            applyPrefill(line);
          })
          .catch(function () {
            // Product fetch failed (bad handle, network): leave the message as is.
          });
      } else if (fromCart) {
        setContext('cart');
        fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .then(function (cart) {
            if (!cart || !cart.items || !cart.items.length) return;
            var lines = [];
            cart.items.forEach(function (item) {
              var line = item.quantity + ' × ' + item.product_title;
              if (item.variant_title && item.variant_title !== 'Default Title') {
                line += ' — ' + item.variant_title;
              }
              if (item.sku) {
                line += ' (SKU ' + item.sku + ')';
              }
              lines.push(line);
            });
            lines.push(T.subtotal + formatMoney(cart.total_price) + T.note);
            applyPrefill(lines.join('\n'));
          })
          .catch(function () {
            // Cart fetch failed: never mutate the cart, just skip prefill.
          });
      } else if (!loadDraft()) {
        setContext('none');
      }
    }

    var existingDraft = loadDraft();
    if (existingDraft) restoreDraft(existingDraft);
    runPrefill();

    trackedFieldIds.forEach(function (id) {
      var el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('input', saveDraft);
      el.addEventListener('change', saveDraft);
    });

    // Duplicate-submit guard: only disables the button after the shopper's
    // own (valid) submit; never submits or blocks submission itself.
    form.addEventListener('submit', function () {
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-disabled', 'true');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
